<template>
    <div>
        <x-modal
            :wWidth="1080"
            v-if="isVisible"
            @cancel="deferredInsuranceModalClose"
            @close="deferredInsuranceModalClose"
            @ok="deferredInsuranceModalSubmit"
            :draggable="true"
            :resizable="true"
            :loading="loading"
            :okDisabled="deferredInsuranceModalButtonDisabled"
            :okText="emailBuilder.sendEmail ? (emailOnly ? 'Send Email' : 'Send Email & Complete Step') : 'Complete Step'"
        >
            <div slot="title" :class="`d-flex justify-content-between align-items-center`">Asset Insurance</div>

            <div class="container">
                <el-form label-width="140px">

                    <email-sending-options></email-sending-options>

                    <email-recipients
                        v-if="emailBuilder.sendEmail || emailBuilder.sendSms"
                        recipientType="applicant"
                        :autoSelectRecipients="true"
                        :recipientAllowCreate="false"
                    ></email-recipients>

                    <email-task v-model="emailBuilder.task"></email-task>

                    <el-divider
                        v-if="!_.isEmpty(emailBuilder.recipients) && (emailBuilder.sendEmail || emailBuilder.sendSms)"
                    ></el-divider>

                    <email-markup-multi
                        class="mb-3"
                        v-if="!_.isEmpty(emailBuilder.recipients) && (emailBuilder.sendEmail || emailBuilder.sendSms)"
                        action="deferred-insurance-selection"
                    ></email-markup-multi>
                </el-form>
            </div>
        </x-modal>
    </div>
</template>

<script>
import EmailSendingOptions from "@ambition/components/messages/EmailSendingOptions.vue"
import EmailRecipients from "@ambition/components/messages/EmailRecipients"
import EmailAttachments from "@ambition/components/messages/EmailAttachments"
import EmailMarkupMulti from "@ambition/components/messages/EmailMarkupMulti"
import EmailTask from "@ambition/components/messages/EmailTask"
import AssetInsurances from "@ambition/components/deferredInsurance/assetInsurances"
import InsuranceTierData from "@ambition/components/single-option/data/InsuranceTierData"
import { getRootState } from "@ambition/components/single-option/data/State"

import { mapGetters } from "vuex"

export default {
    name: "DeferredInsuranceModal",
    props: {
        visible: {
            required: true,
            type: Boolean,
        },
        emailOnly: {
            required: false,
            type: Boolean,
            default: false,
        },
    },
    components: {
        EmailSendingOptions,
        EmailRecipients,
        EmailAttachments,
        EmailMarkupMulti,
        EmailTask,
        AssetInsurances,
    },
    watch: {
        visible(visible) {
            this.isVisible = visible
        },
        transformedInsurances: {
            handler(transformedInsurances) {
                transformedInsurances.forEach((insurance) => {
                    let name
                    switch (insurance.name) {
                        case "Comprehensive":
                            name = "comprehensiveInsurance"
                            break
                        case "CCI/BCI":
                            name = "ccibci"
                            break
                        case "GAP":
                            name = "gap"
                            break
                        case "Warranty":
                            name = "warranty"
                            break
                    }
                    this.selectedInsurances[name] = insurance.options
                })
            },
            deep: true,
        },
    },
    data() {
        return {
            isVisible: false,
            loading: false,
            selectedInsurances: {},
            insurances: [],
            transformedInsurances: [],
        }
    },
    async mounted() {
        try {
            this.getInsurances()
        } catch (err) {
            console.error(err)
        }
        const rootState = await getRootState(this.initAppData.aai)
        if (rootState) this.$store.dispatch("loanOptions/saveCurrentState", rootState)
    },
    activated() {},
    computed: {
        ...mapGetters({
            emailBuilder: "builderEmail/getEmail",
            initAppData: "initAppData",
            currentState: "loanOptions/getCurrentState",
            templatesLoaded: "builderEmail/checkTemplatesLoaded",
        }),
        deferredInsuranceModalButtonDisabled() {
            if (!this.emailBuilder.sendEmail && !this.emailBuilder.sendSms) return false
            if (this.emailBuilder.delaySending && !this.emailBuilder.sendAt) return true
            if (!this.templatesLoaded) return true
            if (_.size(this.emailBuilder.recipients) >= 1 && !this.loading) return false
            return true
        },
    },
    methods: {
        async getInsurances() {
            let adminHeaders = {
                params: {
                    asset_type_id: this.initAppData.asset_type_id,
                },
                headers: { Authorization: "Bearer " + this.$store.getters.adminToken },
            }

            let adminUrl = process.env.MIX_APP_URL

            return new Promise(async (resolve, reject) => {
                const rootState = await getRootState(this.initAppData.aai)
                if (rootState) this.$store.dispatch("loanOptions/saveCurrentState", rootState)

                axios
                    .get(adminUrl + "/api/insurance-tiers", adminHeaders)
                    .then((res) => {
                        // get the unique ids in to an array
                        // update to group based on title (insurance tier type)

                        let titles = Array.from(new Set(res.data.map((a) => a.title)))
                        // iterate through unique ids
                        this.insurances = []

                        for (let title of titles) {
                            let insuranceClass = res.data.filter((insurance) => insurance.title == title)
                            this.insurances.push(insuranceClass)
                        }

                        let tierData = new InsuranceTierData(this.insurances, this.currentState)

                        this.transformedInsurances = tierData.getProductsPerTier()
                        // this.getFirstOption()
                        resolve()
                    })
                    .catch((err) => {
                        reject(err)
                    })
            })
        },
        deferredInsuranceModalClose() {
            this.$store.dispatch("builderEmail/resetEmail", [])
            this.loading = false
            this.isVisible = false
            this.$emit("update:visible", false)
        },
        async deferredInsuranceModalSubmit() {

            this.loading = true

            this.$store.dispatch("insurance/setInsuranceTiers", {force: true})
            this.$store.dispatch("deferredInsurance/setSubmissions")
            this.$store.dispatch("deferredInsurance/setActiveInsurers")

            // create application task
            if (_.get(this.emailBuilder, "task.create", false)) {
                this.$store.dispatch("statusIndicators/changeStatus", {
                    stage: "createTask",
                    token: this.$store.getters.token,
                    remindMe: this.$moment(this.emailBuilder.task.remindMe).format("YYYY-MM-DD"),
                    taskName: this.emailBuilder.task.name,
                    time: this.emailBuilder.task.time,
                    userId: this.emailBuilder.task.userId,
                    appId: this.$store.getters.initAppData.id,
                })
            }

            // create recipient tasks
            if (_.get(this.emailBuilder, "recipients", false)) {
                this.emailBuilder.recipients.forEach((recipient) => {
                    if (_.get(recipient, "template.task.create", false)) {
                        this.$store.dispatch("statusIndicators/changeStatus", {
                            stage: "createTask",
                            token: this.$store.getters.token,
                            remindMe: this.$moment(recipient.template.task.remindMe).format("YYYY-MM-DD"),
                            taskName: recipient.template.task.name,
                            time: recipient.template.task.time,
                            userId: recipient.template.task.userId,
                            appId: this.$store.getters.initAppData.id,
                        })
                    }
                })
            }

            if (!this.emailOnly) {
                if (
                    !this.$store.getters.initAppData.application_status_id ||
                    this.$store.getters.initAppData.application_status_id <= 3
                ) {
                    this.$store.dispatch("statusIndicators/changeStatus", {
                        stage: "documentsReceived",
                        value: 2,
                        resent: false,
                        token: this.$store.getters.token,
                        appId: this.$store.getters.initAppData.id,
                        email: this.emailBuilder,
                        insurances: this.selectedInsurances,
                    })
                    this.$store.dispatch("statusIndicators/changeStatus", {
                        stage: "submittedToLender",
                        value: 1,
                        token: this.$store.getters.token,
                        appId: this.$store.getters.initAppData.id,
                    })
                } else {
                    this.$store.dispatch("statusIndicators/changeStatus", {
                        stage: "documentsReceived",
                        value: 2,
                        resent: true,
                        token: this.$store.getters.token,
                        appId: this.$store.getters.initAppData.id,
                        email: this.emailBuilder,
                        insurances: this.selectedInsurances,
                    })
                }

                this.deferredInsuranceModalClose()

                swal({
                    type: "success",
                    text: "The email has been sent",
                    title: "Success!",
                })

                this.$router.push({ name: "Loan Details" })
            } else {
                axios
                    .post(`/api/applications/${this.$store.getters.initAppData.id}/send-asset-protection-email`, {
                        token: this.$store.getters.token,
                        appId: this.$store.getters.initAppData.id,
                        email: this.emailBuilder,
                        insurances: this.selectedInsurances,
                    })
                    .then((res) => {
                        this.deferredInsuranceModalClose()
                        swal({
                            type: "success",
                            text: "The email has been sent",
                            title: "Success!",
                        })
                        this.$store.dispatch("deferredInsurance/setSubmissions")
                    })
                    .catch((err) => {
                        console.error(err)
                    })
            }
        },
    },
}
</script>
